import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../Contexts/AuthContext';
import { BuildingIcon, SingularIcon, ToolIcon } from '../../assets/menu_icons';
import { AvisosWidgetStyle } from './avisos-widget.style';

const INTERVALO_MS = 6000;
const EMAIL_CONTATO = 'contato@soter.com.br';

interface Aviso {
  /** id da funcionalidade no menuList, vindo da API por cliente */
  id: number;
  titulo: string;
  texto: string;
  rota: string;
  icone: string;
}

/**
 * Ordem do array = prioridade de exibicao.
 *
 * Só entram aqui os modulos que o cliente pode nao saber que tem. Boletos e
 * Ficha Financeira ficam de fora de proposito: o cliente ja procura por eles
 * sozinho, e destacar o obvio dilui o aviso.
 */
const AVISOS: Aviso[] = [
  {
    id: 109,
    titulo: 'Acompanhamento de Obras',
    texto: 'Veja fotos e cronograma da sua obra.',
    rota: '/acompanhamento-da-obra',
    icone: BuildingIcon,
  },
  {
    id: 105,
    titulo: 'Assistência Técnica',
    texto: 'Abra e acompanhe solicitações.',
    rota: '/assistencia',
    icone: ToolIcon,
  },
  {
    id: 107,
    titulo: 'Singular',
    texto: 'Personalize os acabamentos do seu imóvel.',
    rota: '/singular',
    icone: SingularIcon,
  },
];

function prefereMenosMovimento(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export default function AvisosWidget() {
  const { menuList } = useAuth();

  const disponiveis = useMemo(
    () => AVISOS.filter((aviso) => menuList.includes(aviso.id)),
    [menuList],
  );

  const [atual, setAtual] = useState(0);
  const [pausado, setPausado] = useState(false);

  const total = disponiveis.length;

  // Se o menuList encolher (novo login, troca de contrato), o indice guardado
  // pode apontar para fora da lista. Corrigir no render, e nao em efeito:
  // efeito roda depois da pintura, e um acesso fora do array derruba a tela
  // inteira no ErrorBoundary antes disso.
  const indice = atual < total ? atual : 0;

  useEffect(() => {
    if (total < 2 || pausado || prefereMenosMovimento()) return;

    const timer = setInterval(() => {
      setAtual((anterior) => (anterior + 1) % total);
    }, INTERVALO_MS);

    return () => clearInterval(timer);
  }, [total, pausado]);

  // Cliente sem nenhum dos modulos destacaveis: aviso institucional, para o
  // card nunca ficar vazio nem abrir buraco no grid da home.
  if (total === 0) {
    return (
      <AvisosWidgetStyle>
        <div className='avisos-header'>
          <span className='avisos-label'>Avisos</span>
        </div>
        <div className='avisos-corpo institucional'>
          <span className='avisos-titulo'>Precisa de ajuda?</span>
          <a className='avisos-texto' href={`mailto:${EMAIL_CONTATO}`}>
            Fale com a gente em {EMAIL_CONTATO}
          </a>
        </div>
      </AvisosWidgetStyle>
    );
  }

  const aviso = disponiveis[indice];

  return (
    <AvisosWidgetStyle
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
    >
      <div className='avisos-header'>
        <span className='avisos-label'>Avisos</span>

        {total > 1 && (
          <div className='avisos-indicadores'>
            {disponiveis.map((item, posicao) => (
              <button
                key={item.id}
                type='button'
                className={posicao === indice ? 'ativo' : undefined}
                aria-label={`Ver aviso: ${item.titulo}`}
                aria-current={posicao === indice}
                onClick={() => setAtual(posicao)}
              />
            ))}
          </div>
        )}
      </div>

      <Link className='avisos-corpo' to={aviso.rota}>
        <img src={aviso.icone} alt='' aria-hidden='true' />
        <span>
          <span className='avisos-titulo'>{aviso.titulo}</span>
          <span className='avisos-texto'>{aviso.texto}</span>
        </span>
      </Link>
    </AvisosWidgetStyle>
  );
}
